import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X, UserPlus, Loader2 } from "lucide-react";
import { producersMgmtApi } from "./api";
import type { OrganizationType } from "./types";
import { useToast } from "@/app/providers/Toaster";

/**
 * RegisterProducerModal — alta manual DIRECTA de un productor desde Nexus.
 *
 * A diferencia de las invitaciones (email + wizard de activación), esto deja
 * al productor activo al instante: solo tiene que entrar al Hub con su cuenta
 * (Google o email). Si el email no existe, el backend crea la cuenta; si ya
 * existe, la promueve. Idempotente.
 *
 * Ocultar el botón que lo abre no es seguridad — el backend exige
 * PRODUCERS_MGMT_EDIT igual. Este modal es solo la UX.
 */

const ORG_TYPES: { value: OrganizationType; label: string }[] = [
  { value: "club", label: "Club nocturno" },
  { value: "productor", label: "Productor independiente" },
  { value: "agencia", label: "Agencia / Productora" },
  { value: "otro", label: "Otro" },
];

// Catálogo cerrado de regiones de Chile (Mixcore opera solo en CL por ahora).
const CHILE_REGIONS = [
  "Arica y Parinacota",
  "Tarapacá",
  "Antofagasta",
  "Atacama",
  "Coquimbo",
  "Valparaíso",
  "Metropolitana de Santiago",
  "Libertador General Bernardo O'Higgins",
  "Maule",
  "Ñuble",
  "Biobío",
  "La Araucanía",
  "Los Ríos",
  "Los Lagos",
  "Aysén",
  "Magallanes",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// De dónde llegó (embudo de Seguimiento › Seguridad 360).
const SOURCES = [
  { value: "", label: "Sin especificar" },
  { value: "seguridad360", label: "Seguridad 360" },
  { value: "reclutador", label: "Reclutador" },
  { value: "motor", label: "Motor de marketing" },
  { value: "referido", label: "Referido" },
  { value: "instagram", label: "Instagram" },
  { value: "otro", label: "Otro" },
];

export function RegisterProducerModal({ onClose }: { onClose: () => void }) {
  const { push } = useToast();
  const qc = useQueryClient();

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [organizationType, setOrganizationType] = useState<OrganizationType | "">("");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [website, setWebsite] = useState("");
  const [clubName, setClubName] = useState("");
  const [source, setSource] = useState("");
  const [notify, setNotify] = useState(true);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit =
    emailValid &&
    organizationName.trim().length >= 2 &&
    !!organizationType &&
    city.trim().length >= 2 &&
    // El alta por Seguridad 360 es de un club.
    (source !== "seguridad360" || clubName.trim().length >= 2);

  const createMut = useMutation({
    mutationFn: () =>
      producersMgmtApi.createManual({
        email: email.trim().toLowerCase(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        organizationName: organizationName.trim(),
        organizationType: organizationType as OrganizationType,
        region: region.trim() || undefined,
        city: city.trim(),
        website: website.trim() || undefined,
        clubName: clubName.trim() || undefined,
        source: source || undefined,
        notify,
      }),
    onSuccess: (res) => {
      const who = res.user.email;
      const base = res.wasProducer
        ? `${who} ya era productor. ${res.profileCreated ? "Se completó su ficha." : "No se modificó nada."}`
        : res.created
          ? `Cuenta creada y habilitada: ${who}.`
          : `${who} ahora es productor.`;
      const clubMsg = res.clubCreated ? " Club creado y listo para su evento." : "";
      push({ kind: "success", message: base + clubMsg });
      qc.invalidateQueries({ queryKey: ["producers-mgmt"] });
      onClose();
    },
    onError: (err: any) => {
      push({
        kind: "error",
        message:
          err?.response?.data?.error ||
          "No pudimos dar de alta al productor. Intenta de nuevo.",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || createMut.isPending) return;
    createMut.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="nx-card w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <UserPlus size={16} className="text-accent" />
            <h2 className="text-sm font-semibold text-fg">Registrar productor</h2>
          </div>
          <button
            onClick={onClose}
            className="text-fg-subtle hover:text-fg transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="overflow-y-auto px-5 py-4 flex flex-col gap-4">
          <p className="text-xs text-fg-muted">
            El productor queda activo al instante. Si el email no tiene cuenta,
            se crea; luego solo debe entrar al Hub con su cuenta de Google o
            email. No pasa por postulación ni wizard.
          </p>

          {/* ── Cuenta ── */}
          <div>
            <label className="text-2xs text-fg-subtle block mb-1">
              Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="productor@email.com"
              className="nx-input w-full"
              autoFocus
            />
            {email.length > 0 && !emailValid && (
              <p className="text-2xs text-danger mt-1">Email inválido.</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Nombre</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Adiel"
                className="nx-input w-full"
              />
            </div>
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Apellido</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Mora"
                className="nx-input w-full"
              />
            </div>
          </div>
          <p className="text-2xs text-fg-subtle -mt-2">
            Solo se usa si la cuenta es nueva. Si ya existe, se respeta su nombre.
          </p>

          <div className="h-px bg-border-subtle" />

          {/* ── Organización (perfil de catálogo) ── */}
          <div>
            <label className="text-2xs text-fg-subtle block mb-1">
              Nombre comercial <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder="Nombre de la productora / organización"
              className="nx-input w-full"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">
                Tipo <span className="text-danger">*</span>
              </label>
              <select
                value={organizationType}
                onChange={(e) => setOrganizationType(e.target.value as OrganizationType)}
                className="nx-input w-full"
              >
                <option value="">Elegir…</option>
                {ORG_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">
                Ciudad <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Santiago"
                className="nx-input w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Región</label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="nx-input w-full"
              >
                <option value="">Sin especificar</option>
                {CHILE_REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-2xs text-fg-subtle block mb-1">Web / Instagram</label>
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="instagram.com/…"
                className="nx-input w-full"
              />
            </div>
          </div>

          <div className="h-px bg-border-subtle" />

          {/* ── Club (opcional) ── */}
          <div>
            <label className="text-2xs text-fg-subtle block mb-1">
              Nombre del club <span className="text-fg-subtle">· opcional</span>
            </label>
            <input
              type="text"
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
              placeholder="Nombre del club / venue"
              className="nx-input w-full"
            />
            <p className="text-2xs text-fg-subtle mt-1">
              Si lo completas, le dejamos el club creado y listo para crear su
              evento. Si no, lo crea él desde el Hub.
            </p>
          </div>

          <div>
            <label className="text-2xs text-fg-subtle block mb-1">¿Por dónde llegó?</label>
            <select value={source} onChange={(e) => setSource(e.target.value)} className="nx-input w-full">
              {SOURCES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {source === "seguridad360" ? (
              <p className="text-2xs text-fg-subtle mt-1">
                El club queda marcado y aparece en Seguimiento › Seguridad 360. Indica el nombre del
                club (es obligatorio para este origen).
              </p>
            ) : null}
          </div>

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="mt-0.5 accent-[var(--accent,#F0354D)]"
            />
            <span className="text-xs text-fg-muted">
              Enviar email de bienvenida con el link al Hub.
            </span>
          </label>
        </form>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0">
          <button type="button" onClick={onClose} className="nx-btn-secondary">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || createMut.isPending}
            className="nx-btn-primary disabled:opacity-40"
          >
            {createMut.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Dando de alta…
              </>
            ) : (
              <>
                <UserPlus size={14} />
                Registrar productor
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}
